import React, { useRef } from "react";
import { gqlType } from "@hifieng/common";

import PigInfo from "./PigInfo";
import Header from "./Header";
import DetailsSection from "../DetailsSection";
import EventMap from "../EventMap";
import SchematicMap, { SchematicMapDataPointType } from "../SchematicMap";

import { SCREENSHOT_REMOVE_SECTION_CLASS } from "../ShareDialog";
import { useOrganizationContext } from "../../contexts/OrganizationProvider";
import { getStatusText } from "../../helpers/pigStatus";
import { getSchematicMapInputs } from "../../helpers/getSchematicMapInputs";

import styles from "./index.module.scss";
import { METERS_IN_KM } from "../../helpers/constants";
import { useAuth } from "../../contexts/AuthProvider";
import { getUnit, getUnitFlag } from "../../helpers/getOrgUnits";

type PropsType = {
  updateId: string;
  pig: gqlType.PigRun;
  returnUrl: string;
};

const PigUpdateDetails = ({ returnUrl, updateId, pig }: PropsType) => {
  const pigDetailsRef = useRef<HTMLDivElement>(null);
  const targetUpdate = pig.updates.find(
    (update: gqlType.PigRunUpdate) => update.id === updateId
  );
  const { permissions } = useAuth();
  const pigWithTargetUpdate = {
    ...pig,
    updates: targetUpdate ? [targetUpdate] : []
  };

  const { activeOrg } = useOrganizationContext();

  if (!activeOrg || !targetUpdate) {
    return <div className={styles.Wrapper}></div>;
  }

  const { pipelines } = activeOrg;
  const { post, status } = targetUpdate;

  const featuredUpdateData: SchematicMapDataPointType | undefined = {
    id: updateId,
    category: "pig",
    isFeatured: true,
    type: status,
    post
  };

  const pipelineId = targetUpdate.pipelineId
    ? targetUpdate.pipelineId
    : pig.pipeline.id;
  const { assets, domain } = getSchematicMapInputs(pipelines, pipelineId);

  let estimatedArrival;
  if (targetUpdate.nextPoi) {
    const distanceInKm = targetUpdate.nextPoi.post - targetUpdate.post;
    const secondsToArrival = Math.floor(
      (distanceInKm * METERS_IN_KM) / targetUpdate.speed
    );
    estimatedArrival = targetUpdate.timestamp + secondsToArrival;
  }

  return (
    <div ref={pigDetailsRef} id="pig-details" className={styles.Content}>
      <div className={styles.Fixed}>
        <Header
          returnUrl={returnUrl}
          pigDetailsRef={pigDetailsRef}
          updateId={updateId}
          pigId={pig.id}
          status={status}
        />
      </div>
      <div className={styles.Scroll}>
        <DetailsSection>
          <PigInfo
            pig={pig}
            update={targetUpdate}
            nextPoi={
              targetUpdate.nextPoi
                ? `${targetUpdate.nextPoi.type} ${targetUpdate.nextPoi.post}`
                : undefined
            }
            estimatedArrival={estimatedArrival}
          />
        </DetailsSection>
        <DetailsSection
          title="Schematic Map"
          className={SCREENSHOT_REMOVE_SECTION_CLASS}
        >
          <div className={styles.SchematicMapWrapper}>
            {pipelines && featuredUpdateData && (
              <SchematicMap
                key={updateId}
                featuredEvent={featuredUpdateData}
                events={[]}
                assets={assets}
                pipelineDomain={domain}
                iconLabel={getStatusText(status)}
                unit={getUnit("unit", activeOrg, pipelineId, permissions)}
                unitFlag={getUnitFlag(activeOrg, pipelineId, permissions)}
              />
            )}
          </div>
        </DetailsSection>
        <DetailsSection title="Event Map" last>
          <div className={styles.MapWrapper}>
            <EventMap
              events={[pigWithTargetUpdate]}
              canSearchOnMapMove={false}
              isSearching={false}
              focusPipelines={[pipelineId]}
              hideLegend
              disableEventIconSelection
            />
          </div>
        </DetailsSection>
      </div>
    </div>
  );
};

export default PigUpdateDetails;
